/** The artifact kinds the Canvas can preview; everything else is plain "code". */
export type ArtifactType = "html" | "svg" | "mermaid" | "markdown" | "code";

/** A code block opened in the Canvas panel. */
export interface Artifact {
  /** Stable id derived from the message + language + first line (see makeArtifactId). */
  id: string;
  language: string;
  type: ArtifactType;
  code: string;
}

const LANGUAGE_TYPE_MAP: Record<string, ArtifactType> = {
  html: "html",
  htm: "html",
  svg: "svg",
  mermaid: "mermaid",
  mmd: "mermaid",
  markdown: "markdown",
  md: "markdown",
  mdx: "markdown",
};

export function detectArtifactType(language: string): ArtifactType {
  return LANGUAGE_TYPE_MAP[language.toLowerCase().trim()] ?? "code";
}

/** Previewable artifacts get a Code/Preview toggle; plain code opens to code view. */
export function isPreviewable(type: ArtifactType): boolean {
  return type !== "code";
}

const EXTENSION_MAP: Record<string, string> = {
  html: "html",
  htm: "html",
  svg: "svg",
  mermaid: "mmd",
  mmd: "mmd",
  markdown: "md",
  md: "md",
  mdx: "mdx",
  javascript: "js",
  js: "js",
  typescript: "ts",
  ts: "ts",
  python: "py",
  py: "py",
  go: "go",
  rust: "rs",
  java: "java",
  cpp: "cpp",
  c: "c",
  css: "css",
  json: "json",
  yaml: "yaml",
  yml: "yml",
  sql: "sql",
  bash: "sh",
  sh: "sh",
  shell: "sh",
  ruby: "rb",
  php: "php",
  swift: "swift",
  kotlin: "kt",
};

/** A sensible download extension for the artifact's language. */
export function getFileExtension(language: string): string {
  return EXTENSION_MAP[language.toLowerCase().trim()] || language || "txt";
}

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

/**
 * A stable id for a code block: message scope + language + a hash of the FIRST
 * line. Hashing only the first line keeps the id stable as the block's body
 * grows during streaming, so the open Canvas artifact keeps syncing.
 */
export function makeArtifactId(messageId: string, language: string, code: string): string {
  const firstLine = code.split("\n")[0] ?? "";
  return `${messageId}-${language}-${simpleHash(firstLine)}`;
}
